import { kebabCase } from 'es-toolkit';
import React, { RefObject, useState } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FormControl, IconButton, Input, InputAdornment, InputLabel } from '@mui/material';

import { useOnSubmit } from './LoginForm';

interface Props {
  readonly inputRef?: RefObject<HTMLInputElement | null>;

  readonly label: string;
  readonly value: string;

  readonly disabled?: boolean;

  /**
   * Defaults to true
   */
  readonly required?: boolean;
  readonly onChange: (value: string) => void;

  readonly autoFocus?: boolean;
  readonly disableAutoComplete?: boolean;
  readonly password?: boolean;
  readonly error?: boolean;
}

/**
 * The standard way to handle text input within a `LoginForm` component.
 */
export function LoginInput({
  inputRef,
  label,
  value,
  disabled,
  required,
  onChange,
  autoFocus,
  disableAutoComplete,
  password,
  error,
}: Props) {
  const id = kebabCase(label);

  const onSubmit = useOnSubmit();
  const [passwordHidden, setPasswordHidden] = useState(true);
  const togglePasswordHidden = () => setPasswordHidden((previousValue) => !previousValue);

  return (
    <FormControl disabled={disabled} margin="normal" required={required ?? true} fullWidth>
      <InputLabel htmlFor={id} error={error}>
        {label}
      </InputLabel>
      <Input
        sx={{ mt: { xs: 1, md: 2 } }}
        autoComplete={disableAutoComplete ? 'off' : undefined}
        autoFocus={autoFocus}
        type={password && passwordHidden ? 'password' : 'text'}
        id={id}
        value={value}
        error={error}
        inputRef={inputRef}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            onSubmit(event);
          }
        }}
        endAdornment={
          password && (
            <InputAdornment position="end">
              <IconButton
                aria-label="Toggle password visibility"
                tabIndex={-1}
                disabled={disabled}
                onClick={togglePasswordHidden}
              >
                {passwordHidden ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          )
        }
      />
    </FormControl>
  );
}
